import React, { useState, useEffect } from 'react';
import { User, Mail, KeySquare, Shield } from 'lucide-react';
import constant from '../../constant';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const UserForm = ({ initialValues, hospitals, onSubmit, onCancel, isEditMode = false }) => {
  const [formData, setFormData] = useState({
    user_id: initialValues.user_id || '',
    name: initialValues.name || '',
    email: initialValues.email || '',
    password: '',
    role: initialValues.role || '',
    hospital_id: initialValues.hospital_id || '',
    created_on: initialValues.created_on || '',
    created_by: initialValues.created_by || '',
    is_active: initialValues.is_active === undefined ? true : initialValues.is_active,
  });
  const [errors, setErrors] = useState({});
  const [hospitalList, setHospitalList] = useState([]);
  const [hospitalError, setHospitalError] = useState(null);

  useEffect(() => {
    setFormData({
      user_id: initialValues.user_id || '',
      name: initialValues.name || '',
      email: initialValues.email || '',
      password: '',
      role: initialValues.role || '',
      hospital_id: initialValues.hospital_id || '',
      created_on: initialValues.created_on || '',
      created_by: initialValues.created_by || '',
      is_active: initialValues.is_active === undefined ? true : initialValues.is_active,
    });
  }, [initialValues]);

  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setHospitalError('No authentication token found');
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();
        if (response.ok) {
          setHospitalList(result);
        } else {
          setHospitalError(result.message || 'Failed to fetch hospitals');
        }
      } catch (error) {
        setHospitalError('Error fetching hospitals');
      }
    };

    fetchHospitals();
  }, []);

  const validateForm = (data) => {
    const newErrors = {};

    if (!data.name || data.name.trim() === '') {
      newErrors.name = 'Name is required.';
    }
    if (!data.email || data.email.trim() === '') {
      newErrors.email = 'Email is required.';
    } else if (!emailRegex.test(data.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!isEditMode && (!data.password || data.password.trim() === '')) {
      newErrors.password = 'Password is required.';
    }
    if (!data.role || data.role.trim() === '') {
      newErrors.role = 'Role is required.';
    }
    // hospital_id is optional for admin users
    if (data.role !== 'admin' && (!data.hospital_id || data.hospital_id.trim() === '')) {
      newErrors.hospital_id = 'Hospital ID is required for non-admin users.';
    }

    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const payload = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        hospital_id: formData.hospital_id || null, // Set to null if empty
        is_active: formData.is_active,
      };
      if (!isEditMode || formData.password) {
        payload.password = formData.password;
      }
      if (!isEditMode) {
        payload.created_on = new Date().toISOString();
        payload.created_by = 'system'; // Adjust based on your auth system
      }
      onSubmit(payload);
    }
  };

  const getInputClasses = (fieldName) => {
    const baseClasses = "w-full px-4 py-2 border rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
    const errorClasses = "border-red-500 focus:ring-red-500 focus:border-red-500";
    const normalClasses = "border-gray-300";
    return `${baseClasses} ${errors[fieldName] ? errorClasses : normalClasses}`;
  };

  const getIconContainerClasses = (fieldName) => {
    const baseClasses = "inline-flex items-center px-3 rounded-l-md border border-r-0 bg-gray-50 text-gray-500";
    const errorClasses = "border-red-500";
    const normalClasses = "border-gray-300";
    return `${baseClasses} ${errors[fieldName] ? errorClasses : normalClasses}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="space-y-6 bg-white p-6 rounded-lg">
        {hospitalError && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
            {hospitalError}
          </div>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name {errors.name && <span className="text-red-600">*</span>}
              </label>
              <div className="flex">
                <span className={getIconContainerClasses('name')}>
                  <User className="h-4 w-4" />
                </span>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={getInputClasses('name')}
                  aria-invalid={errors.name ? "true" : "false"}
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
              </div>
              {errors.name && <p id="name-error" className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email {errors.email && <span className="text-red-600">*</span>}
              </label>
              <div className="flex">
                <span className={getIconContainerClasses('email')}>
                  <Mail className="h-4 w-4" />
                </span>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={getInputClasses('email')}
                  aria-invalid={errors.email ? "true" : "false"}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
              </div>
              {errors.email && <p id="email-error" className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password {isEditMode ? '(Leave blank to keep current)' : <span className="text-red-600">*</span>}
              </label>
              <div className="flex">
                <span className={getIconContainerClasses('password')}>
                  <KeySquare className="h-4 w-4" />
                </span>
                <input
                  type="password"
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!isEditMode}
                  className={getInputClasses('password')}
                  aria-invalid={errors.password ? "true" : "false"}
                  aria-describedby={errors.password ? "password-error" : undefined}
                />
              </div>
              {errors.password && <p id="password-error" className="mt-1 text-xs text-red-600">{errors.password}</p>}
            </div>

            {/* Role */}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role {errors.role && <span className="text-red-600">*</span>}
              </label>
              <div className="flex">
                <span className={getIconContainerClasses('role')}>
                  <Shield className="h-4 w-4" />
                </span>
                <select
                  name="role"
                  id="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className={getInputClasses('role')}
                  aria-invalid={errors.role ? "true" : "false"}
                  aria-describedby={errors.role ? "role-error" : undefined}
                >
                  <option value="">Select Role</option>
                  <option value="hospital-admin">Hospital Admin</option>
                  <option value="nurse">Nurse</option>
                  <option value="doctor">Doctor</option>
                </select>
              </div>
              {errors.role && <p id="role-error" className="mt-1 text-xs text-red-600">{errors.role}</p>}
            </div>

            {/* Hospital ID */}
            <div>
              <label htmlFor="hospital_id" className="block text-sm font-medium text-gray-700 mb-1">
                Hospital {formData.role !== 'admin' && errors.hospital_id && <span className="text-red-600">*</span>}
              </label>
              <div className="flex">
                <span className={getIconContainerClasses('hospital_id')}>
                  <Shield className="h-4 w-4" />
                </span>
                <select
                  name="hospital_id"
                  id="hospital_id"
                  value={formData.hospital_id}
                  onChange={handleChange}
                  className={getInputClasses('hospital_id')}
                  aria-invalid={errors.hospital_id ? "true" : "false"}
                  aria-describedby={errors.hospital_id ? "hospital_id-error" : undefined}
                  disabled={formData.role === 'admin'} // Disable for admin role
                >
                  <option value="">Select Hospital</option>
                  {hospitalList.map((hospital) => (
                    <option key={hospital.hospital_id} value={hospital.hospital_id}>
                      {hospital.name} 
                    </option>
                  ))}
                </select>
              </div>
              {errors.hospital_id && <p id="hospital_id-error" className="mt-1 text-xs text-red-600">{errors.hospital_id}</p>}
            </div>

            {/* Created On */}
            {isEditMode && formData.created_on && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created On
                </label>
                <input
                  type="text"
                  value={new Date(formData.created_on).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
              </div>
            )}

            {/* Created By */}
            {isEditMode && formData.created_by && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created By
                </label>
                <input
                  type="text"
                  value={formData.created_by}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
              </div>
            )}

{isEditMode && formData.status && (
            <div>
              <label htmlFor="is_active" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="is_active"
                id="is_active"
                value={formData.is_active ? 'Active' : 'Inactive'}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'Active' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
)}
          </div>

          <div className="flex justify-end space-x-4 mt-8">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {isEditMode ? 'Update User' : 'Add User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

UserForm.defaultProps = {
  initialValues: {
    user_id: '',
    name: '',
    email: '',
    password: '',
    role: '',
    hospital_id: '',
    created_on: '',
    created_by: '',
    is_active: true,
  },
  hospitals: [],
};

export default UserForm;